'use client';

import { useId, useState, type FormEvent } from 'react';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { FormError } from '@/components/form-error';
import { useHydrated } from '@/lib/use-hydrated';
import {
  WAITLIST_COOLDOWN_MS,
  WAITLIST_FORM_URL,
  WAITLIST_STORAGE_KEY,
  WAITLIST_LABEL,
} from '../waitlist';

const failureMessage = 'Something went wrong. Please try again.';
const rateLimitMessage = 'Too many signups. Please try again in a little while.';
const responseSchema = z.object({ message: z.string().optional() });

// Inline forms sit on a single row beside their button, as in the landing page's tiles.
const LAYOUTS = {
  inline: {
    container: 'w-full max-w-[420px] min-w-0',
    back: 'self-start text-inherit',
    form: 'grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2.5',
    field: 'min-w-0',
    label: 'sr-only',
    input: 'h-11 bg-background text-foreground disabled:bg-background',
    actions: undefined,
    submit: 'h-11 min-h-11 py-0',
  },
  stacked: {
    container: undefined,
    back: 'self-start',
    form: 'flex flex-col gap-6',
    field: undefined,
    label: undefined,
    input: undefined,
    actions: 'action-row',
    submit: undefined,
  },
};

// Browsers that block site data still submit, leaving rate limiting to the provider.
function lastSignup() {
  try {
    return Number(localStorage.getItem(WAITLIST_STORAGE_KEY));
  } catch {
    return 0;
  }
}

function rememberSignup(time: number | null) {
  try {
    if (time === null) {
      localStorage.removeItem(WAITLIST_STORAGE_KEY);
    } else {
      localStorage.setItem(WAITLIST_STORAGE_KEY, String(time));
    }
  } catch {
    // Nothing to remember without storage.
  }
}

export function WaitlistForm({
  inline = false,
  autoFocus = false,
}: {
  inline?: boolean;
  autoFocus?: boolean;
}) {
  const id = useId();
  const layout = LAYOUTS[inline ? 'inline' : 'stacked'];
  const hydrated = useHydrated();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const now = Date.now();
    const previous = lastSignup();

    if (previous && previous + WAITLIST_COOLDOWN_MS > now) {
      setError(rateLimitMessage);
      setStatus('error');

      return;
    }

    rememberSignup(now);
    setStatus('pending');
    setError('');

    try {
      const response = await fetch(WAITLIST_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ userGroup: '', mailingLists: '', email }).toString(),
      });

      if (!response.ok) {
        if (response.status === 429) {
          setError(rateLimitMessage);
        } else {
          rememberSignup(null);
          const parsed = responseSchema.safeParse(await response.json().catch(() => null));

          setError(parsed.success ? parsed.data.message || failureMessage : failureMessage);
        }

        setStatus('error');

        return;
      }

      setEmail('');
      setStatus('success');
    } catch {
      rememberSignup(null);
      setError(failureMessage);
      setStatus('error');
    }
  }

  return (
    <div className={layout.container}>
      {status === 'success' || status === 'error' ? (
        <div className="flex flex-col gap-4">
          {status === 'success' ? (
            <p role="status">Thanks! We’ll be in touch!</p>
          ) : (
            <FormError error={error} />
          )}
          <Button
            variant="link"
            className={layout.back}
            onClick={() => {
              setError('');
              setStatus('idle');
            }}
          >
            Back
          </Button>
        </div>
      ) : (
        <form
          action={WAITLIST_FORM_URL}
          method="POST"
          onSubmit={submit}
          aria-label="Join the Wolven waitlist"
          className={layout.form}
        >
          <Field className={layout.field}>
            <FieldLabel htmlFor={id} className={layout.label}>
              Email address
            </FieldLabel>
            <div className="focus-marker">
              <Input
                id={id}
                autoFocus={autoFocus}
                type="email"
                name="newsletter-form-input"
                autoComplete="email"
                placeholder="Email address"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                disabled={!hydrated || status === 'pending'}
                className={layout.input}
              />
            </div>
          </Field>
          <div className={layout.actions}>
            <Button
              type="submit"
              disabled={!hydrated || status === 'pending'}
              className={layout.submit}
            >
              {status === 'pending' ? 'Please wait...' : WAITLIST_LABEL}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
